import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import useSearchParams from 'hooks/useSearchParams'

import Search from 'components/Search'
import Table from 'components/Table'

import WistiaVideoRow from 'pages/shared/WistiaVideoRow'

import GA4 from 'helpers/ga4'

import { PageTitle, PageHeader, SinglePanePage } from 'styles/shared'

import Api from 'services/api'

import Resource from 'types/Resource'

const FieldTechHelp = () => {
  const navigate = useNavigate()

  const [refresh, setRefresh] = useState(Date.now())

  const query = useSearchParams('q')

  const [searchValue, setSearchValue] = useState<string>(query)
  const [filteredVideos, setFilteredVideos] = useState<Array<Resource>>([])
  const [searchedValue, setSearchedValue] = useState<string | null>(query)
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    const fetchVideos = async () => {
      if (searchedValue) {
        setShowSpinner(true)

        await Api.WistiaVideo.getFTHVideo(searchedValue)
          .then((res) => {
            setFilteredVideos(res)
            setShowSpinner(false)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, n/handle-callback-err
          }).catch((error) => {
            setFilteredVideos([])
            setShowSpinner(false)
          })
      }
    }

    fetchVideos()
  }, [searchedValue, refresh])

  const onSearchCallback = useCallback((query: string) => {
    setFilteredVideos([])
    if (query !== searchedValue) {
      setShowSpinner(true)

      setSearchedValue(query)
      setRefresh(Date.now())

      GA4.sendEvent({
        action: 'search',
        category: 'Field Tech Help',
        label: query
      })
    }

    navigate(`/field_tech_help?q=${query}`)
  }, [navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SinglePanePage>
      <PageHeader>
        <PageTitle>Field Tech Help</PageTitle>

        <S.Search
          value={searchValue}
          focusOnMount
          placeholder="Search for videos"
          ariaLabel="field tech help"
          onChange={setSearchValue}
          onSearch={onSearchCallback}
          onClickClearIcon={() => { setSearchValue(''); setSearchedValue(null); setShowSpinner(false) }}
          showSpinner={showSpinner}
        />
      </PageHeader>

      {!showSpinner && query.length > 0 &&
      <Table
        noDataMessage={{
          title: 'No videos found',
          description: 'Please try a different search term'
        }}
        items={filteredVideos}
      >
        <Table.Header>
          <S.HeaderCell title="Name" />
          <S.DescriptionHeaderCell title="Description" />
          <S.HeaderCell title="Date" />
        </Table.Header>
        { filteredVideos.map((video) => (
          <S.CardContainer key={video.hashedId}>
            <WistiaVideoRow video={video} key={video.hashedId} />
          </S.CardContainer>
        ))}
      </Table>
    }
    </SinglePanePage>
  )
}

export default FieldTechHelp

const S = {
  CardContainer: styled.div`
    margin: 20px;
    height: 100%;
  `,
  Search: styled(Search)`
    max-width: 300px;
  `,
  HeaderCell: styled(Table.HeaderCell)`
    flex-basis: 25%;
  `,
  DescriptionHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 50%;
  `
}
